/**
 * Pure masonry layout (frontend-architecture §2.1–2.5). No React, no DOM.
 */
import { ASPECT_MAX, ASPECT_MIN, GRID_GAP, GRID_MAX_COLS, LAYOUT_CACHE_SIZE } from '@/config/constants';

export type GridItemBase = { key: string; aspect: number };

export type Pos = { x: number; y: number; w: number; h: number; col: number };

export type LayoutParams = {
  containerWidth: number;
  targetCardWidth: number;
  gap: number;
  footer: number;
};

export type Layout = {
  params: LayoutParams;
  cols: number;
  colWidth: number;
  footer: number;
  gap: number;
  positions: Pos[];
  colHeights: Float64Array;
  colItems: number[][];
  height: number;
  placedCount: number;
  /** cache key this layout was computed for */
  key: string;
};

export function clampAspect(a: number): number {
  if (!Number.isFinite(a) || a <= 0) return 1;
  return a < ASPECT_MIN ? ASPECT_MIN : a > ASPECT_MAX ? ASPECT_MAX : a;
}

export function columnCount(containerWidth: number, targetCardWidth: number, gap = GRID_GAP): number {
  const c = Math.floor((containerWidth + gap) / (targetCardWidth + gap));
  return c < 1 ? 1 : c > GRID_MAX_COLS ? GRID_MAX_COLS : c;
}

export function columnWidth(containerWidth: number, cols: number, gap = GRID_GAP): number {
  return (containerWidth - gap * (cols - 1)) / cols;
}

export function layoutKey(cols: number, colWidth: number, footer: number, itemsVersion: number): string {
  return `${cols}:${colWidth.toFixed(2)}:${footer}:${itemsVersion}`;
}

export function createLayout(params: LayoutParams, itemsVersion: number): Layout {
  const cols = columnCount(params.containerWidth, params.targetCardWidth, params.gap);
  const colWidth = columnWidth(params.containerWidth, cols, params.gap);
  return {
    params,
    cols,
    colWidth,
    footer: params.footer,
    gap: params.gap,
    positions: [],
    colHeights: new Float64Array(cols),
    colItems: Array.from({ length: cols }, () => []),
    height: 0,
    placedCount: 0,
    key: layoutKey(cols, colWidth, params.footer, itemsVersion),
  };
}

/** §2.3 — order-preserving shortest-column placement, incremental from `from`. */
export function place(layout: Layout, items: readonly GridItemBase[], from = layout.placedCount): void {
  const { cols, colWidth, footer, gap, colHeights, colItems, positions } = layout;
  for (let i = from; i < items.length; i++) {
    let col = 0;
    for (let c = 1; c < cols; c++) if (colHeights[c]! < colHeights[col]! - 0.5) col = c;
    const item = items[i]!;
    const h = colWidth / clampAspect(item.aspect) + footer;
    const y = colHeights[col]!;
    positions[i] = { x: col * (colWidth + gap), y, w: colWidth, h, col };
    colItems[col]!.push(i);
    colHeights[col] = y + h + gap;
  }
  if (items.length < positions.length) positions.length = items.length;
  layout.placedCount = items.length;
  let max = 0;
  for (let c = 0; c < cols; c++) if (colHeights[c]! > max) max = colHeights[c]!;
  layout.height = items.length ? max - gap : 0;
}

/** Full layout from scratch. */
export function computeLayout(items: readonly GridItemBase[], params: LayoutParams, itemsVersion = 0): Layout {
  const l = createLayout(params, itemsVersion);
  place(l, items, 0);
  return l;
}

/**
 * LRU cache of layouts keyed by `${cols}:${colWidth}:${footer}:${itemsVersion}` (§2.3).
 * `get` returns an existing layout for the key or computes it. If a layout for the same
 * geometry and `itemsVersion` exists but has fewer placed items (append), placement is
 * extended incrementally in place.
 */
export class LayoutCache {
  private map = new Map<string, Layout>();
  constructor(private readonly max = LAYOUT_CACHE_SIZE) {}

  get(items: readonly GridItemBase[], params: LayoutParams, itemsVersion: number): Layout {
    const cols = columnCount(params.containerWidth, params.targetCardWidth, params.gap);
    const colWidth = columnWidth(params.containerWidth, cols, params.gap);
    const key = layoutKey(cols, colWidth, params.footer, itemsVersion);
    let l = this.map.get(key);
    if (l) {
      this.map.delete(key);
      this.map.set(key, l); // bump recency
      if (l.placedCount < items.length) place(l, items, l.placedCount);
      else if (l.placedCount > items.length) {
        // not an append: recompute (cheap safety; caller should have bumped itemsVersion)
        l = computeLayout(items, params, itemsVersion);
        this.map.set(key, l);
      }
      return l;
    }
    l = computeLayout(items, params, itemsVersion);
    this.map.set(key, l);
    while (this.map.size > this.max) {
      const oldest = this.map.keys().next().value;
      if (oldest === undefined) break;
      this.map.delete(oldest);
    }
    return l;
  }

  clear(): void {
    this.map.clear();
  }

  get size(): number {
    return this.map.size;
  }
}

/**
 * Detects whether `next` is a pure append of `prev` (same object references for the shared
 * prefix). Used to decide incremental placement vs. bumping `itemsVersion`.
 */
export function isAppendOf(prev: readonly GridItemBase[] | null, next: readonly GridItemBase[]): boolean {
  if (!prev) return false;
  if (next.length < prev.length) return false;
  for (let i = 0; i < prev.length; i++) if (prev[i] !== next[i]) return false;
  return true;
}
